import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { IS1Sorting, S1SortService } from '../s1-sort.service';
import { IS1InputSelectItem } from '../s1-input-select/s1-input-select.component';

export type S1ColumnFilterType = 'text' | 'enum' | 'date' | 'custom' | 'sort'; // 'sort': solo ordinamento, senza filtro

@Component({
  selector: 's1-column-filter',
  templateUrl: './s1-column-filter.component.html',
  styleUrls: ['./s1-column-filter.component.scss']
})
export class S1ColumnFilter implements OnInit, OnDestroy {

  @Input() control: UntypedFormControl;         // valore del filtro (text: string; enum: any[])
  @Input() type: S1ColumnFilterType = 'text';
  @Input() options: IS1InputSelectItem[] = [];  // opzioni per type='enum'
  @Input() sortKey: string;                     // campo di ordinamento backend; se assente l'ordinamento è nascosto
  @Input() title: string;                       // intestazione del popover; se assente uso il testo dell'intestazione di colonna

  @Output() apply = new EventEmitter<void>();   // il parent ricarica (reset pagina 1 + ricerca)

  @ViewChild('trigger') triggerRef: ElementRef;

  isOpen: boolean = false;
  titoloPopup: string;
  isActiveSort: boolean = false;
  sortAscending: boolean = true;
  optionSearch: string = '';

  // posizione del popover (position: fixed, per uscire dall'overflow della tabella)
  popTop: number = 0;
  popLeft: number = 0;
  private readonly POP_WIDTH: number = 340;

  private sortSubscription: Subscription;

  constructor(private s1SortService: S1SortService, private el: ElementRef) { }

  ngOnInit(): void {
    this.sortSubscription = this.s1SortService.columnSorted$.subscribe((sorting: IS1Sorting) => {
      this.isActiveSort = !!this.sortKey && sorting.sortBy === this.sortKey;
      if (this.isActiveSort) {
        this.sortAscending = sorting.isSortAscending;
      }
    });
  }

  ngOnDestroy(): void {
    this.sortSubscription?.unsubscribe();
  }

  get hasFilter(): boolean {
    const v = this.control?.value;
    if (v == null) { return false; }
    if (Array.isArray(v)) { return v.length > 0; }
    if (typeof v === 'string') { return v.trim().length > 0; }
    return true;
  }

  toggle(): void {
    if (this.isOpen) {
      this.chiudi();
      return;
    }
    this.isOpen = true;
    this.titoloPopup = this.title || this.testoIntestazioneColonna();
    this.positionPop();
    // dopo il rendering del popover porto il focus sul primo campo di testo, così si può digitare subito e premere Invio
    setTimeout(() => this.focusPrimoCampoTesto());
  }

  private focusPrimoCampoTesto(): void {
    const campi: HTMLInputElement[] = Array.from(this.el.nativeElement.querySelectorAll('.s1cf-pop input[type="text"]'));
    const campo = campi.find(c => !c.closest('ng-select') && !c.readOnly && !c.disabled);
    campo?.focus();
  }

  // Invio in un campo del popover applica il filtro (escluse le tendine ng-select, dove Invio seleziona l'opzione)
  onInvio(event: KeyboardEvent): void {
    const target = event.target as HTMLElement;
    if (!target || event.isComposing || target.closest('ng-select, textarea, button')) {
      return;
    }
    event.preventDefault();
    // faccio confermare il valore al campo (es. datepicker, che lo aggiorna su change) prima di applicare
    target.dispatchEvent(new Event('change', { bubbles: true }));
    setTimeout(() => this.applyFilter());
  }

  // testo dell'intestazione di colonna (th) che contiene il filtro, escluso il filtro stesso
  private testoIntestazioneColonna(): string {
    const th: HTMLElement = this.el.nativeElement.closest('th');
    if (!th) { return null; }
    const testo = Array.from(th.querySelectorAll('span'))
                       .filter((span: HTMLElement) => !this.el.nativeElement.contains(span))
                       .map((span: HTMLElement) => span.innerText.trim())
                       .filter(t => !!t)
                       .join(' ');
    return testo || null;
  }

  private positionPop(): void {
    const rect: DOMRect = this.triggerRef?.nativeElement?.getBoundingClientRect();
    if (!rect) { return; }
    this.popTop = rect.bottom + 4;
    // il popover sta nel contesto di sovrapposizione dell'area contenuti, sotto il menu laterale:
    // non deve oltrepassare il bordo sinistro dell'area contenuti (né il bordo destro della finestra)
    // (il contenitore interno del menu può sbordare oltre il menu stesso: considero il bordo più a destra)
    const contenuti: HTMLElement = this.el.nativeElement.closest('.section-container');
    const inizioContenuti = contenuti ? contenuti.getBoundingClientRect().left + (parseFloat(getComputedStyle(contenuti).paddingLeft) || 0) : 0;
    const bordoMenu = Array.from(document.querySelectorAll('.aside-container, .aside-container .aside-inner'))
                           .reduce((max, e) => Math.max(max, e.getBoundingClientRect().right), 0);
    const minLeft = Math.max(inizioContenuti, bordoMenu + 4, 8);
    const maxLeft = Math.max(window.innerWidth - Math.min(this.POP_WIDTH, window.innerWidth - 16) - 8, minLeft);
    let left = rect.right - this.POP_WIDTH;   // allineo il bordo destro del popover al trigger
    if (left < minLeft) { left = minLeft; }
    if (left > maxLeft) { left = maxLeft; }
    this.popLeft = left;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isOpen) { return; }
    const target = event.target as HTMLElement;
    if (this.el.nativeElement.contains(target)) { return; }
    // ng-select e datepicker montano i loro popup sul body: un click lì non deve chiudere il filtro
    if (target?.closest && target.closest('.ng-dropdown-panel, ng-dropdown-panel, bs-datepicker-container, .bs-datepicker')) { return; }
    this.chiudi();
  }

  @HostListener('window:scroll')
  @HostListener('window:resize')
  onReposition(): void {
    if (this.isOpen) { this.positionPop(); }
  }

  sort(ascending: boolean): void {
    if (!this.sortKey) { return; }
    this.sortAscending = ascending;
    this.s1SortService.sortByColumn({ sortBy: this.sortKey, isSortAscending: ascending });
    this.chiudi();
  }

  // ---- checklist enum ----
  get filteredOptions(): IS1InputSelectItem[] {
    if (!this.optionSearch) { return this.options; }
    const q = this.optionSearch.toLowerCase();
    return this.options.filter(o => o.label.toLowerCase().includes(q));
  }

  isChecked(code: any): boolean {
    const v = this.control?.value;
    return Array.isArray(v) && v.includes(code);
  }

  toggleOption(code: any, checked: boolean): void {
    let v: any[] = Array.isArray(this.control.value) ? [...this.control.value] : [];
    if (checked) {
      if (!v.includes(code)) { v.push(code); }
    } else {
      v = v.filter(x => x !== code);
    }
    this.control.setValue(v.length ? v : null);
  }

  get allChecked(): boolean {
    const v = this.control?.value;
    return Array.isArray(v) && this.options.length > 0 && v.length === this.options.length;
  }

  toggleAll(checked: boolean): void {
    this.control.setValue(checked ? this.options.map(o => o.code) : null);
  }

  // Chiude il popover e riallinea gli altri campi legati allo stesso controllo (es. il campo nel form di ricerca):
  // Angular non propaga a video tra più input dello stesso FormControl i valori digitati in uno di essi
  private chiudi(): void {
    this.isOpen = false;
    this.control?.setValue(this.control.value, { emitEvent: false });
  }

  // ---- azioni ----
  applyFilter(): void {
    this.chiudi();
    this.apply.emit();
  }

  clearFilter(): void {
    this.control.setValue(null);
    this.optionSearch = '';
    this.chiudi();
    this.apply.emit();
  }

}
